import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, authImageUrl } from "@/lib/api";
import { Crown, Layers, Building2, Users, ShieldCheck, ChevronRight, Award } from "lucide-react";

const Avatar = ({ m, size = "w-12 h-12" }) => {
  if (!m) return null;
  return m.photo_url ? (
    <img src={authImageUrl(m.photo_url)} alt={m.cognome}
      className={`${size} rounded-md object-cover border border-gold/40 shrink-0`}
      onError={(e) => { e.target.style.display = "none"; }} />
  ) : (
    <div className={`${size} rounded-md bg-navy flex items-center justify-center border border-gold/40 shrink-0`}>
      <span className="font-display text-gold font-bold">
        {(m.nome?.[0] || "").toUpperCase()}{(m.cognome?.[0] || "").toUpperCase()}
      </span>
    </div>
  );
};

export default function Distretto() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/org/structure").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="text-center py-16 text-navy">Caricamento…</div>;

  return (
    <div className="space-y-10" data-testid="distretto-page">
      <div>
        <p className="text-xs tracking-[0.15em] uppercase font-bold text-gold">Organigramma</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-navy mt-2 tracking-tight">{data.district_name}</h1>
      </div>

      {/* Cariche distrettuali */}
      <section>
        <div className="flex items-center gap-2 mb-5">
          <Crown className="w-5 h-5 text-gold" />
          <h2 className="font-display text-2xl font-semibold text-navy">Cariche Distrettuali</h2>
        </div>
        {data.officers.length === 0 ? (
          <div className="bg-white rounded-lg border border-dashed border-navy/20 p-8 text-center" data-testid="officers-empty">
            <ShieldCheck className="w-8 h-8 text-navy/20 mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">Le cariche distrettuali non sono ancora state configurate.</p>
            <p className="text-muted-foreground text-xs mt-1">Un amministratore può impostarle da <span className="font-semibold text-navy">Admin › Organigramma</span>.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {data.officers.map((o, i) => {
              const card = (
                <div className="bg-white rounded-lg border border-navy/10 p-5 flex items-center gap-4 h-full hover:shadow-md transition-shadow duration-200">
                  <Avatar m={o.member} />
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-wider text-gold font-bold truncate">{o.role}</p>
                    <p className="font-semibold text-navy-deep truncate">{o.name || "—"}</p>
                    {o.member && (o.member.clubs?.length || o.member.club) && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {(o.member.clubs?.length ? o.member.clubs : [o.member.club]).filter(Boolean).join(" · ")}
                      </p>
                    )}
                  </div>
                </div>
              );
              return o.member
                ? <Link key={i} to={`/soci/${o.member.id}`} data-testid={`officer-${i}`}>{card}</Link>
                : <div key={i} data-testid={`officer-${i}`}>{card}</div>;
            })}
          </div>
        )}
      </section>

      {/* Divisioni con i club */}
      <section>
        <div className="flex items-center gap-2 mb-5">
          <Layers className="w-5 h-5 text-gold" />
          <h2 className="font-display text-2xl font-semibold text-navy">Divisioni & Club <span className="text-muted-foreground text-base font-sans">({data.divisions.length})</span></h2>
        </div>

        <div className="space-y-6">
          {data.divisions.map((d) => (
            <div key={d.number} className="bg-white rounded-lg border border-navy/10 overflow-hidden" data-testid={`division-block-${d.number}`}>
              {/* header divisione */}
              <div className="bg-navy text-white px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                <div className="min-w-0">
                  <h3 className="font-display text-lg sm:text-xl font-bold leading-snug">{d.name}</h3>
                  <div className="flex items-center gap-4 text-xs text-white/70 mt-1">
                    <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{d.clubs_count} club</span>
                    <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{d.members_count} soci</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {d.lieutenant ? (
                    <Link to={`/soci/${d.lieutenant.id}`} className="flex items-center gap-2 bg-white/10 hover:bg-white/20 rounded-md px-3 py-1.5 transition-colors" data-testid={`lieutenant-link-${d.number}`}>
                      <Award className="w-4 h-4 text-gold" />
                      <div className="text-left leading-tight">
                        <p className="text-[9px] uppercase tracking-wider text-gold font-bold">Luogotenente</p>
                        <p className="text-sm font-semibold">{d.lieutenant.nome} {d.lieutenant.cognome}</p>
                      </div>
                    </Link>
                  ) : (
                    <span className="text-xs text-white/50 italic flex items-center gap-1"><Award className="w-4 h-4" />Luogotenente non assegnato</span>
                  )}
                </div>
              </div>

              {/* elenco club */}
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {d.clubs.map((c) => {
                  const clickable = !!c.data_club;
                  return (
                    <button key={c.name} disabled={!clickable}
                      onClick={() => clickable && navigate(`/club/${encodeURIComponent(c.data_club)}`)}
                      data-testid={`club-item-${c.name}`}
                      className={`group text-left rounded-md border p-3 transition-colors duration-200 ${clickable ? "border-navy/10 hover:border-gold hover:bg-gold/5" : "border-dashed border-navy/10 opacity-60 cursor-default"}`}>
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold text-navy-deep text-sm leading-snug">{c.name}</p>
                        {clickable && <ChevronRight className="w-4 h-4 text-gold shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />}
                      </div>
                      {c.president ? (
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <Crown className="w-3 h-3 text-gold" />{c.president.nome} {c.president.cognome}
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground/70 mt-1 italic">Presidente non indicato</p>
                      )}
                      <p className="text-[11px] text-muted-foreground mt-1">
                        {clickable
                          ? `${c.members_count - (c.honorary_count || 0)} soci${c.honorary_count ? ` + ${c.honorary_count} onorari` : ""} · vedi elenco`
                          : "nessun socio in elenco"}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
